/**
 * Canonical API paths + safe fetch helpers for agent workspace builders.
 */

import localApiAdapter from '../../core/services/localApiAdapter';

export const AGENT_API = {
  facturation: {
    relances: '/facturation/relances',
    archives: '/archives',
    clients: '/facturation/clients',
    fournisseurs: '/facturation/fournisseurs',
  },
  inventaire: {
    stockAlerts: '/inventory/stock-alerts',
    movements: '/inventory/movements',
    warehouses: '/inventory/warehouses',
    replenishments: '/inventory/replenishments',
    pickings: '/inventory/pickings',
    documents: '/inventaire/documents',
  },
  achats: {
    demandes: '/achats/demandes',
    commandes: '/purchase-orders',
    receptions: '/receptions',
    livre: '/achats/livre-des-achats',
    documents: '/achats/documents',
  },
  ventes: {
    commandes: '/ventes/sales-orders',
    livraisons: '/ventes/livraisons',
    pipeline: '/ventes/pipeline',
    documents: '/ventes/documents',
  },
  rh: {
    employees: '/hr/employees',
    conges: '/pointage/conges',
    departures: '/hr/departures',
    documents: '/hr/documents',
  },
  pointage: {
    absences: '/timetracking/absences',
    lateness: '/timetracking/lateness',
    overtime: '/timetracking/overtime',
    daily: '/timetracking/daily',
    conges: '/pointage/conges',
    documents: '/pointage/documents',
  },
  salaires: {
    journal: '/salaries/journal',
    validation: '/salaries/validation',
    virements: '/salaires/virements-bancaires',
    cnas: '/salaries/cnas',
    documents: '/salaries/documents',
  },
  situation: {
    incidents: '/gestion-situation/incidents',
    crises: '/gestion-situation/crises',
    plans: '/gestion-situation/plans-intervention',
    documents: '/gestion-situation/documents',
  },
  caisse: {
    journal: '/treasury/journal',
    vouchers: '/treasury/vouchers',
    reconciliation: '/treasury/reconciliation',
    documents: '/treasury/documents',
  },
  banque: {
    accounts: '/banking/accounts',
    reconciliation: '/banking/reconciliation/general',
    checks: '/banking/checks',
    transfers: '/banking/transfers',
    documents: '/banking/documents',
  },
  comptabilite: {
    entries: '/comptabilite/entries',
    balance: '/situation/balance',
    journal: '/situation/journal',
    documents: '/comptabilite/documents',
  },
  tresorerie: {
    position: '/tresorerie/position',
    investments: '/tresorerie/investments',
    liquidity: '/tresorerie/liquidity',
    documents: '/tresorerie/documents',
  },
  budget: {
    variance: '/budget/variance-monitoring',
    workflow: '/budget/workflow',
    engagements: '/budget/engagements',
    documents: '/budget/documents',
  },
  projets: {
    tasks: '/projects/tasks',
    alerts: '/projects/alerts',
    milestones: '/projects/milestones',
    resources: '/projects/resources',
    documents: '/projects/documents',
  },
  contrats: {
    expiryAlerts: '/contracts/expiry-alerts',
    clients: '/contracts/clients',
    approvals: '/api/contracts/approvals',
    renewals: '/contracts/renewals',
    documents: '/contracts/documents',
  },
  production: {
    orders: '/production/orders',
    breakdowns: '/production/breakdowns',
    nonconformities: '/production/quality/nonconformities',
    documents: '/production/documents',
  },
  maintenance: {
    tickets: '/maintenance/tickets',
    emergency: '/maintenance/emergency',
    workorders: '/maintenance/workorders',
    planning: '/maintenance/planning',
    documents: '/maintenance/documents',
  },
  qualite: {
    nonConformites: '/qualite/non-conformites',
    audits: '/qualite/audits',
    actions: '/qualite/actions-correctives',
    documents: '/qualite/documents',
  },
  import_export: {
    importDecl: '/import-export/import-declarations',
    exportDecl: '/import-export/export-declarations',
    shipping: '/import-export/shipping',
    inspections: '/import-export/inspections',
    documents: '/import-export/documents',
  },
  parc_auto: {
    vehicles: '/fleet/vehicles',
    maintenance: '/fleet/maintenance',
    insurance: '/fleet/insurance',
    violations: '/fleet/violations',
    documents: '/fleet/documents',
  },
  logistique: {
    incidents: '/logistics/incidents-delays',
    deliveries: '/logistics/deliveries',
    manifests: '/logistics/transport-manifests',
    documents: '/logistique/documents',
  },
  distribution: {
    planning: '/distribution/planning',
    routes: '/distribution/routes',
    orderPrep: '/distribution/order-preparation',
    documents: '/distribution/documents',
  },
  livraison: {
    ongoing: '/livraison/ongoing',
    refused: '/livraison/refused-deliveries',
    proofs: '/livraison/delivery-proofs',
    documents: '/livraison/documents',
  },
  point_de_vente: {
    sales: '/pos/sales',
    stock: '/pos/stock',
    payments: '/pos/payments',
    documents: '/pos/documents',
  },
  marketing: {
    campaigns: '/marketing/campaigns',
    budget: '/marketing/budget',
    events: '/marketing/events',
    documents: '/marketing/documents',
  },
  crm: {
    leads: '/crm/leads',
    opportunities: '/crm/opportunities',
    tickets: '/crm/support-tickets',
    documents: '/crm/documents',
  },
  service_client: {
    tickets: '/service-client/tickets',
    sla: '/service-client/sla',
    reclamations: '/service-client/reclamations',
    documents: '/service-client/documents',
  },
  taches: {
    tasks: '/taches/tasks',
    reminders: '/taches/reminders',
    milestones: '/taches/milestones',
    documents: '/taches/documents',
  },
  approbations: {
    mes: '/approbations/mes-approbations',
    workflows: '/approbations/workflows',
    statuts: '/approbations/statuts',
    documents: '/approbations/documents',
  },
  planification: {
    rappels: '/planification/rappels/pending',
    rendezvous: '/planification/rendez-vous/upcoming',
    charge: '/planification/charge/summary',
    calendrier: '/planification/calendrier',
    documents: '/planification/documents',
  },
};

export async function safeGet(path, fallback = []) {
  if (!path) return fallback;
  try {
    const data = await localApiAdapter.get(path);
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.results)) return data.results;
    return fallback;
  } catch {
    return fallback;
  }
}

export async function safeGetDocuments(moduleId) {
  try {
    if (moduleId === 'facturation') {
      const docs = await localApiAdapter.documents?.getDocuments?.();
      return Array.isArray(docs) ? docs : [];
    }
    const paths = AGENT_API[moduleId]?.documents;
    if (paths) return safeGet(paths);
    return [];
  } catch {
    return [];
  }
}

export async function fetchParallel(pathMap = {}) {
  const entries = Object.entries(pathMap);
  const results = await Promise.all(entries.map(([, path]) => safeGet(path)));
  return Object.fromEntries(entries.map(([key], index) => [key, results[index]]));
}

/** Thin adapters for modules that previously used raw fetch in page components. */
export const agentModuleFetch = {
  caisse: {
    journal: () => safeGet(AGENT_API.caisse.journal),
    vouchers: () => safeGet(AGENT_API.caisse.vouchers),
    reconciliation: () => safeGet(AGENT_API.caisse.reconciliation),
    documents: () => safeGet(AGENT_API.caisse.documents),
  },
  banque: {
    accounts: () => safeGet(AGENT_API.banque.accounts),
    reconciliation: () => safeGet(AGENT_API.banque.reconciliation),
    checks: () => safeGet(AGENT_API.banque.checks),
    transfers: () => safeGet(AGENT_API.banque.transfers),
    documents: () => safeGet(AGENT_API.banque.documents),
  },
  salaires: {
    journal: () => safeGet(AGENT_API.salaires.journal),
    validation: () => safeGet(AGENT_API.salaires.validation),
    virements: () => safeGet(AGENT_API.salaires.virements),
    cnas: () => safeGet(AGENT_API.salaires.cnas),
    documents: () => safeGet(AGENT_API.salaires.documents),
  },
};
